import { useCallback, useState } from 'react';
import { useStore } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import type { RootState } from '../store';
import { useAuth } from './useAuth';
import {
  toggleLike as toggleLikeAction,
  selectLikedContent,
  selectLikesError
} from '../store/slices/likesSlice';
import { ContentType } from '../services/firebase/likes.service';
import logger from '../utils/logger';

const EMPTY_LIKED_CONTENT: Record<string, boolean> = {};

export interface UseLikesOptions {
  /**
   * Subscribe to the liked-content map so isLiked() re-renders when likes
   * change. Detail pages need this. Feed-item buttons (which only call
   * toggleLike) must pass false — the map changes identity on every like
   * anywhere in the app, and subscribing made a like on ONE post re-render
   * every other post's buttons (the "everything flashes" beta report).
   */
  trackLikedContent?: boolean;
}

/**
 * Enhanced hook for handling likes functionality across different content types
 * This hook provides both redux state management and local loading state
 */
export const useLikes = ({ trackLikedContent = true }: UseLikesOptions = {}) => {
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Get error state from Redux
  const reduxError = useAppSelector(selectLikesError);

  // Combined error from both sources
  const error = localError || reduxError;

  // Reactive liked-content map, only when the caller needs isLiked to update
  const likedContent: Record<string, boolean> = useAppSelector(
    trackLikedContent ? selectLikedContent : () => EMPTY_LIKED_CONTENT
  );

  /**
   * Toggle like status for a content item
   * @param contentId ID of the content to like/unlike
   * @param like True to like, false to unlike
   * @param contentType Type of content being liked
   * @returns Promise resolving to success status
   */
  const toggleLike = useCallback(async (
    contentId: string,
    like?: boolean,
    contentType: ContentType = 'post',
    contentOwner?: string
  ) => {
    if (!user) {
      setLocalError('You must be logged in to like content');
      return false;
    }

    // Read like/pending state at call time instead of subscribing — these
    // maps change identity on every like in the app, and subscribing made
    // every mounted like button re-render on every tap
    const { likedContent: likedContentStatus, pendingLikes } = store.getState().likes;
    const currentLikeStatus = likedContentStatus[contentId] || false;
    const isPending = pendingLikes[contentId] || false;

    // Don't allow operations on pending content
    if (isPending) {
      return false;
    }

    // Auto-detect like value if not provided
    const shouldLike = like !== undefined ? like : !currentLikeStatus;

    // Prepare user profile data for the like action
    const userProfile = {
      username: user.username || user.displayName || user.email?.split('@')[0] || 'User',
      displayName: user.displayName || '',
      photoURL: user.photoURL || null
    };

    try {
      setIsLocalLoading(true);
      setLocalError(null);

      // Dispatch the Redux action which handles optimistic updates
      const result = await dispatch(toggleLikeAction({
        contentId,
        contentType,
        like: shouldLike,
        userId: user.uid,
        userProfile,
        contentOwner
      })).unwrap();

      setIsLocalLoading(false);
      return result.liked === shouldLike;
    } catch (err) {
      logger.error('Error toggling like:', err);
      setLocalError(err instanceof Error ? err.message : 'Failed to update like status');
      setIsLocalLoading(false);
      return false;
    }
  }, [user, dispatch, store]);

  /**
   * Like a content item
   * @param contentId ID of the content to like
   * @param contentType Type of content being liked
   * @returns Promise resolving to success status
   */
  const like = useCallback((contentId: string, contentType: ContentType = 'post') => {
    return toggleLike(contentId, true, contentType);
  }, [toggleLike]);

  /**
   * Unlike a content item
   * @param contentId ID of the content to unlike
   * @param contentType Type of content being unliked
   * @returns Promise resolving to success status
   */
  const unlike = useCallback((contentId: string, contentType: ContentType = 'post') => {
    return toggleLike(contentId, false, contentType);
  }, [toggleLike]);

  /**
   * Check if a content item is liked by the current user
   * @param contentId ID of the content to check
   * @returns Boolean indicating if content is liked
   */
  const isLiked = useCallback((contentId: string) => {
    return likedContent[contentId] || false;
  }, [likedContent]);

  // Loading state for THIS hook instance's in-flight toggle only. The old
  // implementation also flipped this on for any pending like anywhere,
  // which made every like button in the feed show a spinner per tap.
  const isLoading = isLocalLoading;

  return {
    like,
    unlike,
    toggleLike,
    isLiked,
    isLoading,
    error
  };
};

// Export both as named and default export for flexibility
export default useLikes;
